import type { Product, Image as ShopifyImage } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import CompareAtPrice from "@components/atoms/CompareAtPrice";
import Image from "@components/atoms/Image";

import useLayout from "@hooks/reducerHooks/useLayout";
import useDropDate from "@hooks/utilsHooks/useDropDate";

import { URLS } from "@const/urls";

import { showArchiveSalePrice } from "@utils/archive-sale";
import type { IProductLaunch } from "@utils/product";
import { getAvailableSize, getProductLaunch } from "@utils/product";

import { fetchVariantBackup } from "@api/lamdaApi";

interface IProps {
  product: Product;
  title: string;
  handle: string;
  featuredImage?: ShopifyImage;
  vendor: string;
  price: number;
  compareAtPrice: number;
  available?: boolean | null;
  tags: string[];
  removeBtn?: React.ReactNode;
  launchData: IProductLaunch;
  availableForSale: boolean;
  rankedNumber?: number;
  isSaleShutDown?: boolean;
}

function ProductCard({
  product,
  title,
  handle,
  availableForSale = true,
  featuredImage,
  vendor,
  removeBtn,
  compareAtPrice,
  price,
  tags,
  rankedNumber,
  isSaleShutDown,
}: IProps) {
  const imageUrl = featuredImage ? featuredImage.url : "";
  const imageWidth = featuredImage?.width ? featuredImage.width : 400;
  const imageHeight = featuredImage?.height ? featuredImage.height : 500;
  const familySalePrice = showArchiveSalePrice({ orignPrice: price, compareAtPrice, tags });

  const launchData = getProductLaunch(product);

  const { dropTimer, dropStatus } = useDropDate(launchData);

  const { closeSearch } = useLayout();

  const [size, setSize] = useState<{ value: string; name: string }[]>([{ value: "-1", name: "" }]);

  useEffect(() => {
    // 상품 목록에서 품절인지 아닌지 판단하기 위해서 호출
    getSize();
  }, [product]);

  const getSize = async () => {
    const sizeList = getAvailableSize(product);

    const keyList = sizeList.map((item) => ({
      variantId: { N: item.value.replace("gid://shopify/ProductVariant/", "") },
    }));

    if (!keyList.length) {
      setSize([]);
      return;
    }

    const result = await fetchVariantBackup(keyList);

    if (result) {
      const stockData: Record<string, number> = {};
      result.forEach((item) => {
        stockData[item.variantId.N] = Math.max(parseInt(item.quantity.N || item.quantity.S || "0"), 0);
      });

      const resultList = sizeList.filter((size) => {
        const id = size.value.replace("gid://shopify/ProductVariant/", "");
        return stockData[id] && stockData[id] > 0;
      });

      setSize(resultList);
    } else {
      const resultList = product.variants.edges.map(({ node }) => ({
        value: node.id,
        name: node.selectedOptions.find((item) => item.name === "Size")?.value || "",
        quantity: node.quantityAvailable,
      }));
      setSize(resultList);
    }
  };

  const isOfflineOnly = tags.includes("Offline Only");

  const productPrice = isSaleShutDown ? price : familySalePrice;
  const salePercent = ((compareAtPrice - productPrice) / compareAtPrice) * 100;
  const productInfoText = () => {
    if (rankedNumber || isOfflineOnly) return `${rankedNumber}`;
    return compareAtPrice > productPrice ? `${salePercent}%` : "";
  };

  return (
    <Link
      className="inline-block h-auto w-full"
      onClick={closeSearch}
      to={tags.includes("Draw") && !tags.includes("OnSale") ? `${URLS.DRAW}/${handle}` : `${URLS.PRODUCTS}/${handle}`}
    >
      <div className={classNames("relative mb-2.5", removeBtn && "z-1")}>
        <Image alt={title} className="size-full" height={imageHeight} src={imageUrl} width={imageWidth} />
        {removeBtn ?? removeBtn}
        {Boolean(productInfoText()) && (
          <div
            className={classNames("absolute top-2.5 left-2.5 z-10 rounded-sm bg-white px-2.5 py-1", {
              "text-status-finished": rankedNumber && rankedNumber > 0 && rankedNumber < 4,
            })}
          >
            {productInfoText()}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-0.5">
        <h2>{vendor}</h2>
        <span>{title}</span>

        {launchData.time && dropStatus !== "Released" && !tags.includes("OnSale") ? (
          <span className={classNames("truncate", dropTimer.includes("Draw Closed") && "text-secondary")}>
            {dropTimer}
          </span>
        ) : (
          <>
            {isOfflineOnly ? (
              <>
                {compareAtPrice > 0 && productPrice > 0 && (
                  <CompareAtPrice originalPrice={compareAtPrice} salePrice={productPrice} />
                )}
              </>
            ) : (
              <>
                {!isNaN(productPrice) &&
                  productPrice > 0 &&
                  (!availableForSale || !size.length ? (
                    <span className="text-secondary">SOLD OUT</span>
                  ) : (
                    <CompareAtPrice originalPrice={compareAtPrice} salePrice={productPrice} />
                  ))}
              </>
            )}
          </>
        )}
      </div>
    </Link>
  );
}

export default ProductCard;
